import UseAxios from '../../Api/http/http';
import { fetchUserInfo } from '../../Api/request/integration';
import { conferenceData, departmentData, designationData, verticalData } from '../../Api/request/master';
import {
  addMappedSkills,
  getFilteredSkills,
  updateMappedSkill,
} from '../../Api/request/skillMap';
import { getSkills } from '../../Api/request/skills';
import { getSkillVerticalDepartmentMap } from '../../Api/request/skillVerticalMap';
import { STATUS_CODES } from '../../Api/resources/statusCodes';
import {
  AUTHENTICATION_FAILED,
  NO_RECORDS_FOUND,
  OOPS_TRY_AGAIN,
  SKILL_MAP_DASHBOARD,
} from '../../Constants/strings';
import { logError } from '../../Utils/logger';

export const fetchDepartmentList = async () => {
  try {
    const response = await UseAxios(departmentData());
    const result = response.data.departmentDetails.map((dept) => ({
      id: dept.id,
      name: dept.deptName,
    }));
    return result;
  } catch (error) {
    return [];
  }
};

export const fetchDesignationList = async () => {
  try {
    const response = await UseAxios(designationData());
    const result = response.data.designationDetails.map((designation) => ({
      id: designation.id,
      name: designation.designationName,
    }));
    return result;
  } catch (error) {
    return [];
  }
};

export const fetchVertivalList = async () => {
  try {
    const response = await UseAxios(verticalData());
    const result = response.data.verticalDetails.map((vertical) => ({
      id: vertical.id,
      name: vertical.verticalName,
    }));
    return result;
  } catch (error) {
    return [];
  }
};

export const fetchConferenceList = async () => {
  try {
    const response = await UseAxios(conferenceData());
    const result = Array.isArray(response.data.data)
      ? response.data.data.map((conference) => ({
        id: conference.id,
        name: conference.name,
      }))
      : [];
    return result;
  } catch (error) {
    return [];
  }
};

export const fetchSkillList = async () => {
  try {
    const response = await UseAxios(getSkills());
    const result = response.data.skillDetails.map((skill) => ({
      id: skill.id,
      name: skill.skillName,
    }));
    return result;
  } catch (error) {
    return [];
  }
};

export const fetchEmployeeList = async () => {
  try {
    const data = {
      filterValues: {},
      fieldsToShow: ['employeeId', 'photoSignedUrl', 'fullName'],
    };

    const response = await UseAxios(fetchUserInfo(data));
    const list = response?.data?.users?.data || [];

    const result = list.map((employee) => ({
      id: employee.employeeId,
      name: employee.fullName,
      ...employee,
    }));
    return result;
  } catch (error) {
    logError(error);
  }
};

export const filterSkillMap = async (data) => {
  const filter = {
    department: data.department.map((dept) => dept.name),
    vertical: data.vertical.map((vert) => vert.name),
    userId: data.employee.map((emp) => emp.id),
    skillId: data.skill.map((skill) => skill.id),
  };

  try {
    const response = await UseAxios(getFilteredSkills(filter));

    if (response.data?.response?.status) {
      const status = response.data.response.status;
      const errorMessages = {
        [STATUS_CODES.NO_DATA]: NO_RECORDS_FOUND,
        [STATUS_CODES.UNAUTHORIZED]: AUTHENTICATION_FAILED,
        [STATUS_CODES.INTERNAL_SERVER_ERROR]: OOPS_TRY_AGAIN,
      };

      return {
        noData: errorMessages[status] || OOPS_TRY_AGAIN,
      };
    } else if (Array.isArray(response.data) && response.data.length === 0) {
      return {
        noData: NO_RECORDS_FOUND,
      };
    } else {
      return response.data;
    }
  } catch (error) {
    return {
      noData: OOPS_TRY_AGAIN,
    };
  }
};

export const editMappedSkills = async (data) => {
  try {
    const response = await UseAxios(updateMappedSkill(data));
    if (response.data?.response?.status) {
      switch (response.data?.response?.status) {
      case STATUS_CODES.UNAUTHORIZED:
        return {
          failure: AUTHENTICATION_FAILED,
        };
      case STATUS_CODES.BAD_REQUEST:
      case STATUS_CODES.INTERNAL_SERVER_ERROR:
        return {
          failure: OOPS_TRY_AGAIN,
        };
      default:
        return response.data;
      }
    } else {
      return response.data;
    }
  } catch (error) {
    return {
      failure: OOPS_TRY_AGAIN,
    };
  }
};

export const mapSkills = async (data) => {
  try {
    const response = await UseAxios(addMappedSkills(data));
    if (response.data?.response?.status) {
      switch (response.data?.response?.status) {
      case STATUS_CODES.BAD_REQUEST:
        return {
          existingSkillIds: response.data?.response?.data?.existingSkillIds,
          failure: SKILL_MAP_DASHBOARD.MAP_SKILL.DUPLICATE_MAP_MESSAGE,
        };
      case STATUS_CODES.UNAUTHORIZED:
        return {
          failure: AUTHENTICATION_FAILED,
        };
      case STATUS_CODES.INTERNAL_SERVER_ERROR:
        return {
          failure: OOPS_TRY_AGAIN,
        };
      default:
        return response.data;
      }
    } else {
      return response.data;
    }
  } catch (error) {
    return {
      failure: OOPS_TRY_AGAIN,
    };
  }
};

export const fetchEmployeeInfo = async (fullName) => {
  try {
    const data = {
      filterValues: { fullName: fullName },
      fieldsToShow: [
        'employeeId',
        'fullName',
        'emailId',
        'department',
        'vertical',
        'photoSignedUrl',
      ],
    };

    const response = await UseAxios(fetchUserInfo(data));

    return response?.data?.users?.data.slice(0, 5) || [];
  } catch (error) {
    logError('Error fetching user info:', error);
    throw error;
  }
};

export const getSkillVertDeptMap = async (verticalId, departmentId) => {
  try {
    const response = await UseAxios(getSkillVerticalDepartmentMap(verticalId, departmentId));

    if (response.data?.response?.status) {
      switch (response.data?.response?.status) {
      case STATUS_CODES.UNAUTHORIZED:
        return {
          failure: AUTHENTICATION_FAILED,
        };
      case STATUS_CODES.NO_DATA:
        return {
          failure: NO_RECORDS_FOUND,
        };
      case STATUS_CODES.INTERNAL_SERVER_ERROR:
        return {
          failure: OOPS_TRY_AGAIN,
        };
      default:
        return response?.data;
      }
    } else {
      return response?.data;
    }
  } catch (error) {
    logError('Error fetching skill vertical map:', error);
    return {
      failure: OOPS_TRY_AGAIN,
    };
  }
};

export const handleClickOutside = (event, isDirty, setIsModalOpen) => {
  if (
    event.target.closest('.sub-menu-item')
    || event.target.closest('.menu-item')
    || event.target.closest('.log-out')
    || event.target.closest('.MuiAutocomplete-listbox')
    || event.target.closest('.sub-menu-item-options')
    || event.target.closest('[aria-label="open drawer"]') // while opening sidebar in mobile view
  ) {
    // Show modal only if there are unsaved changes
    if (isDirty) {
      setIsModalOpen(true);
    }
  }
};

export const fetchProfilePic = async (userIds) => {
  try {
    const data = {
      filterValues: { employeeId: userIds },
      fieldsToShow: ['employeeId', 'photoSignedUrl'],
    };

    const response = await UseAxios(fetchUserInfo(data));
    const list = response?.data?.users?.data || [];
    return list;
  } catch (error) {
    logError(error);
    return [];
  }
};

export const handleBeforeUnload = (event, isDirty) => {
  if (isDirty) {
    event.preventDefault();
    event.returnValue = '';
  }
};
